import React, { useRef } from 'react';
import { Col, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { coloursArray, createQueryString } from '../../../../../../lib/utils';
import { fetchDefectsDetectionRate } from '../../../../../../modules/dashboard';
import ChartTitle from '../../../../../elements/ChartTitle';
import NoDataAvailable from '../NoDataAvailable';
import useExecutionScope from '../../../../../../hooks/useExecutionScope';

const DefectDetectionRate = ({
  dateFilters,
  isJiraIntegrated,
  selectedJiraProject,
}) => {
  const { projectId } = useParams();
  const { isLoading: isScopeLoading, isPlatformsDefined } =
    useExecutionScope(projectId);

  const jiraProjectId = selectedJiraProject?.linked_project_id;

  const forceRefreshRef = useRef(false);
  const queryClient = useQueryClient();
  const queryKey = [
    'defectsDetectionRate',
    projectId,
    dateFilters,
    jiraProjectId,
  ];

  const {
    data: defectData = { moduleChartData: [], platformChartData: [] },
    isLoading,
    isFetching,
  } = useQuery({
    queryKey,
    enabled: isJiraIntegrated && !!selectedJiraProject,
    queryFn: async () => {
      const queryString = createQueryString(dateFilters);
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const response = await fetchDefectsDetectionRate(
        projectId,
        queryString,
        jiraProjectId,
        forceRefresh
      );

      const moduleChartData = (response?.module_data || []).map(
        (item, index) => ({
          module_name: item.name,
          defect_count: item.count,
          fill: coloursArray[index % coloursArray.length],
        })
      );

      const platformChartData = (response?.platform_data || []).map(
        (item, index) => ({
          platform_name: item.name,
          defect_count: item.count,
          fill: coloursArray[index % coloursArray.length],
        })
      );

      return { moduleChartData, platformChartData };
    },
  });

  const { moduleChartData, platformChartData } = defectData;

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({ queryKey, exact: true });
  };

  return (
    <>
      <Col md={12}>
        <div className="chart-div">
          <ChartTitle
            title="Defect Detection Rate in Executions by Modules"
            showWarning={!isJiraIntegrated}
            dateFilters={dateFilters}
            onRefresh={handleRefresh}
            isFetching={isFetching}
            showRefreshButton={!isLoading && isJiraIntegrated}
          />
          {isLoading ? (
            <Spinner className="mt-5" />
          ) : moduleChartData.length === 0 ? (
            <NoDataAvailable />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                barSize={25}
                data={moduleChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="module_name"
                  label={{
                    value: 'Modules',
                    position: 'insideBottom',
                    offset: -10,
                  }}
                  tick={{ fontSize: 12 }}
                />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar
                  dataKey="defect_count"
                  name="Defects"
                  shape={({ x, y, width, height, index }) => (
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      fill={moduleChartData[index]?.fill}
                    />
                  )}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Col>
      {!isScopeLoading && isPlatformsDefined && (
        <Col>
          <div className="chart-div">
            <ChartTitle
              title="Defect Detection Rate in Executions by Platforms"
              showWarning={!isJiraIntegrated}
              dateFilters={dateFilters}
              onRefresh={handleRefresh}
              isFetching={isFetching}
              showRefreshButton={!isLoading && isJiraIntegrated}
            />
            {isLoading ? (
              <Spinner className="mt-5" />
            ) : platformChartData.length === 0 ? (
              <NoDataAvailable />
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    barSize={25}
                    data={platformChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="platform_name"
                      label={{
                        value: 'Platforms',
                        position: 'insideBottom',
                        offset: -10,
                      }}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar
                      dataKey="defect_count"
                      name="Defects"
                      shape={({ x, y, width, height, index }) => (
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={platformChartData[index]?.fill}
                        />
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
            )}
          </div>
        </Col>
      )}
    </>
  );
};

export default DefectDetectionRate;
